// core
import {
  Component, Input, Inject, OnInit, Renderer2
} from '@angular/core';
import { FormGroup } from '@angular/forms';

// campfire
import { ScriptLoaderService } from '@campfire-nx/api';

// services
import { RaptorDataService } from '../../../services/raptor-data.service';
import { RaptorStepService } from '../../../services/raptor-step.service';
import { RaptorAnalyticsService } from '../../../services/raptor-analytics.service';
import { RaptorStepDataService } from '../../../services/raptor-step-data.service';
import { RaptorPriceService } from '../../../services/raptor-price.service';

@Component({
  selector: 'open-raptor-page-hollard-car-covers',
  templateUrl: './raptor-page-hollard-car-covers.component.html',
  styleUrls: ['./raptor-page-hollard-car-covers.component.scss']
})
export class RaptorPageHollardCarCoversComponent implements OnInit {
  @Input() formData: any;
  @Input() form!: FormGroup;

  layoutSettings: any = {};
  bannerHeaders!: any;
  frequencyControl!: any;
  coverLevelControl: any;
  coverTypeControl: any;
  covers!: Array<any>;
  coverLevel!: any;
  productSlugControl: any;
  disclaimers?: Array<any>;
  includedExtrasDisclaimers?: Array<any>;
  currency!: string;
  currencySymbol!: string;
  layoutConfig: any;
  columns: number = 2;
  componentQuestions: Array<any> = [];
  stars = new Array(5);
  ratingId = 'rich-rating-badge';

  constructor(
    public raptorStepService: RaptorStepService,
    public raptorDataService: RaptorDataService,
    public raptorPriceService: RaptorPriceService,
    private raptorAnalyticsService: RaptorAnalyticsService,
    private raptorStepDataService: RaptorStepDataService,
    private scriptLoaderService: ScriptLoaderService,
    private renderer: Renderer2,
    @Inject('environment') private environment: any
  ) { }

  ngOnInit(): void {
    this.layoutSettings = this.formData.settings || {};
    this.coverLevelControl = this.form.get('cover_level');
    this.coverTypeControl = this.form.get('cover_type');
    this.productSlugControl = this.form.get('product_slug');
    this.frequencyControl = this.form.get('frequency');
    this.currency = this.formData?.product?.country?.currency;
    this.currencySymbol = this.formData?.product?.country?.currency_symbol;

    this.layoutConfig = this.raptorDataService.columnLayout(this.formData.sections[0].settings.columns, this.formData.sections[0].settings.rows, this.formData.sections[0].elements, this.formData.sections[0].components);
    this.componentQuestions = this.layoutConfig.questions;

    this.coverLevel = this.formData.data.cover_level;
    this.setCoverLevel(true);
    this.filterCovers();

    // If there are no covers of the selected cover type, then switch the cover_type,
    // reset the cover level, and re-filter the covers.
    if (!this.covers.length) {
      this.formData.data.cover_type = (this.formData.data.cover_type === 'comp') ? 'tp' : 'comp';
      this.coverLevel = null;
      this.setCoverLevel();
      this.coverLevelControl.setValue(this.coverLevel, { emitEvent: false });
      this.coverTypeControl.setValue(this.formData.data.cover_type, { emitEvent: false });
      this.filterCovers();
    }

    if (this.formData.review_widget) {
      // if we use this in the future then move this function to a service
      this.displayRoductReview();
    }
  }

  get frequency() {
    return this.frequencyControl.value;
  }

  filterCovers() {
    this.covers = this.formData.covers.filter((cover: any) => (
      cover.cover.cover_level === this.coverLevel &&
        cover.cover.cover_type === this.formData.data.cover_type
    ));

    this.coverLevelControl.setValue(this.coverLevel, { emitEvent: true });
    this.getBanners(this.covers);

    // update filtered covers to include PAF banners if available
    this.covers = this.covers.map((cover: any) => {
      // if monthly has arrangement fee, so does annual by default
      const hasArrangFeeExtra = cover.premium.M.extras?.arrang_fee?.charge_upfront ?? false;

      if (cover.has_additional_charges && hasArrangFeeExtra) {
        cover.additionalChargesBanners = this.getPolicyArrangementBannerDisclaimer(cover, this.disclaimers);
      }
      return cover;
    });
  }

  setCoverLevel(emitEvent: boolean = false) {
    if (!this.coverLevel) {
      if (this.formData.data.cover_type === 'tp') {
        this.coverLevel = 'tppd';
      } else if (this.formData.data.cover_type === 'comp') {
        if (this.formData.data.kms_estimated <= 15000) {
          this.coverLevel = 'payd';
        } else {
          this.coverLevel = 'comp';
        }
      }
      this.coverLevelControl.setValue(this.coverLevel, { emitEvent: true });
    }
  }

  selectCover(cover: any) {
    this.coverLevelControl.setValue(cover.cover.cover_level, { emitEvent: false });
    this.productSlugControl.setValue(cover.cover.product_slug, { emitEvent: false });
    setTimeout(() => {
      const form = document.getElementById('coverSubmit');
      form?.click();
    }, 500);
  }

  getCover(cover: any): any {
    const formData = this.raptorStepDataService.get();
    return formData.covers.find((item: any) => item.cover.product_slug === cover.cover.product_slug &&
        item.cover.cover_type === cover.cover.cover_type &&
        item.cover.cover_level === cover.cover.cover_level);
  }

  getCoverFeatures(cover: any): any {
    const updatedCover = this.getCover(cover);
    return updatedCover.features;
  }

  getCoverInfo(cover: any, property: string): any {
    const feature = cover.is_featured ? 'featured' : 'regular';
    let coverInfo!: any;
    let info = '';

    if (
      this.formData.settings.details &&
      this.formData.settings.details[feature][cover.cover.cover_type] &&
      this.formData.settings.details[feature][cover.cover.cover_type][cover.cover.cover_level]
    ) {
      coverInfo = this.formData.settings.details[feature][cover.cover.cover_type][cover.cover.cover_level];
    }

    if (coverInfo && coverInfo[property]) {
      info = coverInfo[property];
    }
    return info;
  }

  getPolicyArrangementMonthlyHeading(cover: any) {
    const formData = this.raptorStepDataService.get();
    const updatedCover = formData.covers.find((item: any) => item.cover.product_slug === cover.cover.product_slug &&
        item.cover.cover_type === cover.cover.cover_type &&
        item.cover.cover_level === cover.cover.cover_level);
    const updatedAdditionalChargesBanners = this.getPolicyArrangementBannerDisclaimer(updatedCover, this.disclaimers);

    return updatedAdditionalChargesBanners.monthlyHeading;
  }

  getBanners(covers: any) {
    this.disclaimers = this.raptorDataService.mapBannerDisclaimers(covers);

    if (this.formData.banners?.length) {
      this.bannerHeaders = this.formData.banners.filter((banner: any) => banner.type === 'header');
      this.bannerHeaders.forEach((banner: any) => this.disclaimers?.push({ type: 'banner', content: banner.disclaimer }));
    }
  }

  getPolicyArrangementBannerDisclaimer(cover: any, disclaimers: any): any {
    const formData = this.raptorStepDataService.get();
    const updatedCover = this.getCover(cover);
    const formValues = this.form.value;
    const frequency = formValues.frequency || formData.data.frequency;

    // set disclaimers in the footer
    this.includedExtrasDisclaimers = this.raptorDataService.getIncludedExtrasDisclaimers(updatedCover.premium[frequency].extras);
    return this.raptorDataService.getArrangementFeeBanner(updatedCover.premium, disclaimers as [], frequency, true);
  }

  getTagLabel(banner: any) {
    return this.raptorDataService.mapBannerTag(banner, this.disclaimers);
  }

  openPdsDocument(url: string) {
    this.raptorAnalyticsService.trackAdditionalEvents('select-option', 'pds');
    window.open(`${this.environment.apiEndpoint}${url}`, '_blank');
  }

  displayRoductReview() {
    let settings = this.formData.review_widget;
    // Load Intercom script
    if (settings.identifier && settings.brandId) {
      let trustpilotScript = document.getElementById('trustpilot');
      if (!trustpilotScript) {
        this.scriptLoaderService.loadJsScript(
          this.renderer,
          null,
          'text/javascript',
          'https://cdn.productreview.com.au/assets/widgets/loader.js',
          'trustpilot'
        );
      
        (window as any).__productReviewSettings = {
          brandId: settings.brandId
        };
      } else {
        // this widget can't bind a second time to the same id which is weird
        // dynamically update the id of the container so that the script can bind to it
        this.ratingId = 'rich-rating-badge' + Date.now();
      }

      (window as any).__productReviewCallbackQueue = (window as any).__productReviewCallbackQueue || [];
      (window as any).__productReviewCallbackQueue.push((ProductReview: any) => {
        ProductReview.use('rich-rating-badge', {
          'container': '#' + this.ratingId,
          'identificationDetails': {
            'type': 'single',
            'strategy': 'from-internal-entry-id',
            'identifier': settings.identifier
          }
        });
      });
    }
  }
}
